import { checkFormSlug, FORM_LIMITS, formFieldProblem, formPublishProblem, isFormBlock, type FormRule } from "@omnabase/shared";
import { keyProblem, type Draft, type DraftField, type DraftInput } from "./formEdits";

/** The form builder, round one: the tag settings' edits and what keeps a draft from saving, as pure functions (the companion of
 *  `formEdits.ts`). The builder shows each problem where it is (a field's line in the panel, a rule's line, the address's line) and keeps
 *  Save disabled while any is left, so a mistake shows at once and never only on Save. */

type TagList = "ticket" | "customer";
const withTags = (d: Draft, tags: Draft["tags"]): Draft => ({ ...d, tags });

export const addTag = (d: Draft, list: TagList, id: string): Draft => (d.tags[list].includes(id) ? d : withTags(d, { ...d.tags, [list]: [...d.tags[list], id] }));
export const removeTag = (d: Draft, list: TagList, id: string): Draft => withTags(d, { ...d.tags, [list]: d.tags[list].filter((x) => x !== id) });

/** the plain selects a rule may name (question 18: selects only; the project field is not one) */
export const ruleFields = (d: Draft): DraftInput[] => d.items.filter((f): f is DraftInput => f.type === "select" && f.mapping !== "project" && (f.options?.length ?? 0) > 0);
/** a new rule on the first plain select and its first option, tagging nothing yet */
export function addRule(d: Draft): Draft {
  const f = ruleFields(d)[0];
  if (!f) return d;
  return withTags(d, { ...d.tags, rules: [...d.tags.rules, { field: f.key, equals: f.options![0]!.value, ticket: [], customer: [] }] });
}
/** a rule's field (its first option with it) or option */
export function setRule(d: Draft, index: number, patch: Partial<Pick<FormRule, "field" | "equals">>): Draft {
  const rules = d.tags.rules.map((r, i) => {
    if (i !== index) return r;
    if (patch.field === undefined || patch.field === r.field) return { ...r, ...patch };
    const f = d.items.find((x) => x.key === patch.field);
    return { ...r, field: patch.field, equals: (f && !isFormBlock(f) && f.options?.[0]?.value) || "" };
  });
  return withTags(d, { ...d.tags, rules });
}
export function setRuleTag(d: Draft, index: number, list: TagList, id: string, on: boolean): Draft {
  const rules = d.tags.rules.map((r, i) => (i !== index ? r : { ...r, [list]: on ? (r[list].includes(id) ? r[list] : [...r[list], id]) : r[list].filter((x) => x !== id) }));
  return withTags(d, { ...d.tags, rules });
}
export const removeRule = (d: Draft, index: number): Draft => withTags(d, { ...d.tags, rules: d.tags.rules.filter((_, i) => i !== index) });

export type DraftProblems = { fields: Map<string, string>; keys: Map<string, string>; rules: Map<number, string>; slug: string | null; publish: string | null; /** builder v2: the name edited in place */ name: string | null };
/** every problem of a draft, by place */
export function draftProblems(d: Draft): DraftProblems {
  const fields = new Map<string, string>(), keys = new Map<string, string>(), rules = new Map<number, string>();
  for (const f of d.items) {
    const k = keyProblem(d, f.uid, f.key); if (k) keys.set(f.uid, k);
    const p = itemProblem(f); if (p) fields.set(f.uid, p);
  }
  d.tags.rules.forEach((r, i) => { if (r.ticket.length + r.customer.length === 0) rules.set(i, "Pick a tag for this rule, or remove it."); });
  const slug = checkFormSlug(d.slug) ? "Use lower-case letters, digits and single hyphens (at most 40)." : null;
  const publish = d.published ? formPublishProblem({ fields: { items: d.items }, defaultProjectId: d.defaultProjectId }) : null;
  const name = !d.name.trim() ? "Give the form a name." : d.name.trim().length > FORM_LIMITS.name ? `At most ${FORM_LIMITS.name} characters.` : null;
  return { fields, keys, rules, slug, publish, name };
}
/** one item's own problem: a Text block needs words (the text blocks round, question 9), a field a label and its own rules */
function itemProblem(f: DraftField): string | null {
  if (isFormBlock(f)) return f.text.trim() ? null : "Write the text, or delete this block.";
  return !f.label.trim() ? "Give the field a label." : formFieldProblem(f);
}
export const hasProblems = (p: DraftProblems): boolean => p.fields.size + p.keys.size + p.rules.size > 0 || !!p.slug || !!p.publish || !!p.name;
